import React, { useEffect, useMemo, useState } from 'react';
import { getNewRepos, isAiRepo, RepoSearchResult } from '../../utils/github';
import { formatInt } from '../../utils/format';
import { DataBadge } from '../DataBadge';
import { SourceRef } from '../SourceRef';
import { PageSources } from '../PageSources';
import { ChipGroup } from '../ChipGroup';
type Filter = 'ai' | 'all';
export function NewReposTable() {
  const [result, setResult] = useState<RepoSearchResult | null>(null);
  const [filter, setFilter] = useState<Filter>('all');
  useEffect(() => {
    let alive = true;
    getNewRepos().then(r => alive && setResult(r));
    return () => {
      alive = false;
    };
  }, []);
  const repos = useMemo(() => {
    const list = result?.repos ?? [];
    return filter === 'ai' ? list.filter(isAiRepo) : list;
  }, [result, filter]);
  const aiCount = useMemo(() => (result?.repos ?? []).filter(isAiRepo).length, [result]);
  return <section aria-labelledby="new-repos-title" data-id="element-173" data-mp="3egdz6qtxtr2i">
      <div className="flex flex-wrap items-end justify-between gap-3" data-id="element-174" data-mp="30ylfzs41x9zj">
        <div data-id="element-175" data-mp="310xxy59t7kcl">
          <h2 id="new-repos-title" className="font-serif text-2xl text-ink md:text-3xl" data-id="element-176" data-mp="2vzqo80o3wb3a">
            Top new repos by stars · past 7 days · GitHub Search API
            <SourceRef id="githubSearch" href={result?.apiUrl} grade="A" data-id="element-177" data-mp="3a134kd76iepp" />
          </h2>
          {result?.mode === 'live' && <p className="mt-1 font-mono text-[11.5px] text-muted" data-id="element-178" data-mp="360127nhytmji">{formatInt(result.total)} repos created this week</p>}
        </div>
      </div>
      <PageSources data-id="element-179" data-mp="3ez4nmy4lue16">
        <DataBadge mode={result ? result.mode : 'loading'} source="GitHub Search API · new repos" asOf={result?.asOf} data-id="element-180" data-mp="3exl7d7iy82ze" />
      </PageSources>

      {result?.mode === 'snapshot' && <p className="mt-4 border-l-2 border-machine bg-machine/[0.06] px-4 py-3 text-[13px] text-ink/90" data-id="element-181" data-mp="3onwcgxedci7v">
          This week’s list is temporarily unavailable. Showing notable recent repos with approximate star counts instead.
        </p>}

      <div className="mt-5 flex flex-wrap items-center justify-between gap-3" data-id="element-182" data-mp="1ljaxd2liua4">
        <ChipGroup label="Show" value={filter} onChange={setFilter} options={[['all', `All repos (${result?.repos.length ?? '…'})`], ['ai', `AI / agent repos (${result ? aiCount : '…'})`]]} data-id="element-183" data-mp="2vd9ba9zan7n2" />

      </div>

      <div className="mt-4 overflow-x-auto rounded-lg border border-line" data-id="element-184" data-mp="34atgfu0a1o2y">
        <table className="w-full min-w-[1000px] border-collapse text-[13.5px]" data-id="element-185" data-mp="3944mvu16g2uc">
          <thead className="bg-surface text-left text-[12px] text-muted" data-id="element-186" data-mp="3e2pex669zg7p">
            <tr data-id="element-187" data-mp="3j1adz29sjtuk">
              <th scope="col" className="border-b border-line px-3 py-2.5 text-right font-normal" data-id="element-188" data-mp="3j0r7qiti6twd">#</th>
              <th scope="col" className="border-b border-line px-3 py-2.5 font-normal" data-id="element-189" data-mp="3nzu3tdri9ps7">Repository</th>
              <th scope="col" className="border-b border-line px-3 py-2.5 font-normal" data-id="element-190" data-mp="3s2gv9dk9l8ov">Description</th>
              <th scope="col" className="border-b border-line px-3 py-2.5 font-normal" data-id="element-191" data-mp="1tn48crlho8ip">Language</th>
              <th scope="col" className="border-b border-line px-3 py-2.5 text-right font-normal" data-id="element-192" data-mp="1xn5zw7xiflk9">Stars</th>
              <th scope="col" className="border-b border-line px-3 py-2.5 font-normal" data-id="element-193" data-mp="1q5ghp0c77ibr">Created</th>
              <th scope="col" className="border-b border-line px-3 py-2.5 font-normal" data-id="element-194" data-mp="1lcncycgqc9zo">Topics</th>
            </tr>
          </thead>
          <tbody data-id="element-195" data-mp="1gev8yk2t8xf4">
            {!result && <tr data-id="element-196" data-mp="1cdjt369qnqk4">
                <td colSpan={7} className="px-3 py-12 text-center font-mono text-xs text-muted" role="status" data-id="element-197" data-mp="1ce1nrygr5wpx">
                  Querying GitHub…
                </td>
              </tr>}
            {repos.map((r, i) => <tr key={r.fullName} className="border-b border-line/60 align-top last:border-b-0 hover:bg-surface" data-id="element-198" data-mp="244zgh2j4dhbe" data-mpi={i}>
                <td className="px-3 py-2.5 text-right font-mono text-[12px] text-muted" data-id="element-199" data-mp="204fi5vmt87no">{i + 1}</td>
                <td className="max-w-[240px] px-3 py-2.5" data-id="element-200" data-mp="1v5ud9iwm4zsw">
                  <a href={r.url} target="_blank" rel="noopener noreferrer" className="break-words font-mono text-[12.5px] text-ink hover:text-machine" data-id="element-201" data-mp="oo7e1prqco9w">
                    {r.fullName}
                  </a>
                </td>
                <td className="max-w-[360px] px-3 py-2.5 text-ink/80" data-id="element-202" data-mp="27ixxuj9y2oz3">
                  <span className="line-clamp-2" title={r.description} data-id="element-203" data-mp="2cdbwcujwlc7z">
                    {r.description || <span className="text-faint" data-id="element-204" data-mp="1ylnkmsdkgprj">No description</span>}
                  </span>
                </td>
                <td className="whitespace-nowrap px-3 py-2.5 text-muted" data-id="element-205" data-mp="22mhcfwu4qopp">{r.language ?? '—'}</td>
                <td className="whitespace-nowrap px-3 py-2.5 text-right font-mono tabular text-ink" data-id="element-206" data-mp="2qc1so7ywf7va">
                  {formatInt(r.stars)}
                  <SourceRef href={`https://api.github.com/repos/${r.fullName}`} label={`${r.fullName} — GitHub API`} grade="A" figure={r.fullName} data-id="element-207" data-mp="2hau4wju0dpfg" />
                </td>
                <td className="whitespace-nowrap px-3 py-2.5 font-mono text-[12px] text-muted" data-id="element-208" data-mp="2m83x278s8f6z">{r.created}</td>
                <td className="max-w-[220px] px-3 py-2.5" data-id="element-209" data-mp="15y1ilojzlt6s">
                  <div className="flex flex-wrap gap-1" data-id="element-210" data-mp="1avjiyqjxv036">
                    {r.topics.slice(0, 4).map(t => <span key={t} className="rounded bg-raised px-1.5 py-px font-mono text-[10.5px] text-muted" data-id="element-211" data-mp="12c6ito32q4a9">
                        {t}
                      </span>)}
                  </div>
                </td>
              </tr>)}
            {result && repos.length === 0 && <tr data-id="element-212" data-mp="17arf5cf7g3tf">
                <td colSpan={7} className="px-3 py-10 text-center text-muted" data-id="element-213" data-mp="jn9nrhzur7qz">
                  No AI or agent repos in this week’s top 100.
                </td>
              </tr>}
          </tbody>
        </table>
      </div>
    </section>;
}